/**
 * ProjectDetailPage Component
 *
 * Main project detail page showing:
 * - Project information
 * - List of batches with "Create Batch" button
 * - Entry points to Batch/Job workflows
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../hooks/useProjects';
import { useBatches, useCreateBatch, useCreateBatchFromCSV } from '../hooks/useBatches';
import { BatchCard } from '../components/batches/BatchCard';
import { CreateBatchForm } from '../components/batches/CreateBatchForm';
import { BatchUploadForm } from '../components/batches/BatchUploadForm';
import toast from 'react-hot-toast';

export const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [showUploadCSV, setShowUploadCSV] = useState(false);

  // Fetch project data
  const { data: project, isLoading: projectLoading, error: projectError } = useProject(id);

  // Fetch batches for this project
  const { data: batchesData, isLoading: batchesLoading } = useBatches({ project_id: id });
  const batches = batchesData?.batches || [];

  // Mutations
  const createBatchMutation = useCreateBatch();
  const uploadCSVMutation = useCreateBatchFromCSV();

  const handleCreateBatch = async (data: { name?: string; project_id: string }) => {
    try {
      const batch = await createBatchMutation.mutateAsync(data);
      toast.success('Batch created successfully');
      setShowCreateBatch(false);
      // Navigate to jobs list for this batch
      navigate(`/batches/${batch.batch_id}/jobs`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create batch');
    }
  };

  const handleUploadCSV = async (formData: {
    file: File;
    name: string;
    description: string;
    parsing_description: string;
    parsing_concurrency: number;
    execution_concurrency: number;
  }) => {
    try {
      const batch = await uploadCSVMutation.mutateAsync({
        file: formData.file,
        data: {
          project_id: id!,
          name: formData.name,
          description: formData.description,
          parsing_description: formData.parsing_description,
          parsing_concurrency: formData.parsing_concurrency,
          execution_concurrency: formData.execution_concurrency,
        },
      });
      toast.success('CSV uploaded successfully! Batch created with parsing code.');
      setShowUploadCSV(false);
      // Navigate to batch detail page
      navigate(`/batches/${batch.batch_id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload CSV');
    }
  };

  const handleViewBatch = (batchId: string) => {
    // Navigate to batch detail page (same view as after CSV upload)
    navigate(`/batches/${batchId}`);
  };

  const handleManageJobs = (batchId: string) => {
    // Navigate to batch detail page with Jobs tab (default)
    navigate(`/batches/${batchId}`);
  };

  if (projectLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <svg
          className="animate-spin h-8 w-8 text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-red-800">Error loading project</h3>
            <p className="mt-1 text-sm text-red-700">{projectError.message}</p>
            <button
              onClick={() => navigate('/projects')}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
            >
              Back to projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <nav className="text-sm text-gray-500 mb-2">
                <button onClick={() => navigate('/projects')} className="hover:text-gray-700">
                  Projects
                </button>
                <span className="mx-2">/</span>
                <span className="text-gray-900">{project.name}</span>
              </nav>
              <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
              {project.description && (
                <p className="mt-2 text-gray-600">{project.description}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/projects/${id}`)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Edit Project
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Batches Section */}
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Batches</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateBatch(true)}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
              >
                + Create Batch
              </button>
              <button
                onClick={() => setShowUploadCSV(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
              >
                Upload CSV
              </button>
            </div>
          </div>

          {batchesLoading ? (
            <div className="text-center py-8 text-gray-500">Loading batches...</div>
          ) : batches.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-500 mb-4">No batches yet. Create a batch to start executing jobs.</p>
              <button
                onClick={() => setShowCreateBatch(true)}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
              >
                Create First Batch
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {batches.map((batch) => (
                <BatchCard
                  key={batch.batch_id}
                  batch={batch}
                  onViewResults={handleViewBatch}
                  onManageJobs={handleManageJobs}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Create Batch Modal */}
      {showCreateBatch && (
        <CreateBatchForm
          projectId={id!}
          onSubmit={handleCreateBatch}
          onCancel={() => setShowCreateBatch(false)}
          isLoading={createBatchMutation.isPending}
        />
      )}

      {/* Upload CSV Modal */}
      {showUploadCSV && (
        <BatchUploadForm
          projectId={id!}
          onSubmit={handleUploadCSV}
          onCancel={() => setShowUploadCSV(false)}
          isLoading={uploadCSVMutation.isPending}
        />
      )}
    </div>
  );
};
